import { useEffect, useMemo, useRef, useState } from 'react'
import type {
  MediaAssetPublic,
  ProposalPublic,
  RevisionPublic,
  RoutePublic,
  Waypoint,
} from '@xingroute/schema'
import { ApiError, api } from '../api/client.js'
import { useAuth } from '../auth/AuthContext.js'
import { useLocale } from '../locale/LocaleContext.js'
import { canApprovePublishRoute, canModerateRouteProposals } from '../lib/routePermissions.js'
import { friendlyError } from '../lib/friendlyError.js'
import { formatRelative } from '../lib/relativeTime.js'
import { AuthorizedMediaImage } from './AuthorizedMediaImage.js'

// Enriched types: backend now includes resolved display names alongside IDs.
type EnrichedRevision = RevisionPublic & { editor_name?: string | null }
type EnrichedProposal = ProposalPublic & { author_name?: string | null }
type EnrichedMedia = MediaAssetPublic & { uploaded_by_name?: string | null }

// RouteCollabPanel — revision timeline + proposals for a single route.

const METADATA_DIFF_FIELDS = [
  'title',
  'summary',
  'activities',
  'status',
  'difficulty',
  'route_type',
  'distance_km',
  'ascent_m',
  'duration_seconds',
  'cover_image_url',
  'description_url',
  'geometry_url',
  'region',
  'natural_features',
  'seasonality',
] as const

const STRUCTURED_DIFF_FIELDS = new Set<string>(['route_geometry', 'route_media'])

// Side-effect / internal fields that change on every edit but carry no
// human-meaningful diff. Hidden so a revision diff always surfaces the real
// change (waypoints, media, metadata) instead of bare `updated_at` noise.
const NOISE_DIFF_FIELDS = new Set<string>([
  'updated_at',
  'created_at',
  'schema_version',
  'geometry_url',
  'description_url',
  'route_point_count',
  'point_count',
])

const METADATA_FIELD_ORDER: readonly string[] = [...METADATA_DIFF_FIELDS, 'route_description_md']

type DiffField = string

interface SnapshotCache {
  [revId: string]: Record<string, unknown> | 'loading' | 'error'
}

interface NormalizedSnapshot {
  data: Record<string, unknown>
}

const PROPOSAL_STATUSES: ReadonlyArray<
  'open' | 'approved' | 'rejected' | 'stale' | 'expired' | 'withdrawn'
> = ['open', 'approved', 'rejected', 'stale', 'expired', 'withdrawn']

export interface RouteCollabPanelProps {
  routeId: string
  route?: RoutePublic
  routeWaypoints?: readonly Waypoint[]
  routeMedia?: readonly MediaAssetPublic[]
  routeCreatorId?: string
  routeCreatorName?: string
  routeRegion?: RoutePublic['region']
  routeCreatedAt?: string
  etag?: string
  token?: string | null
  onRevert?: (result: { route: RoutePublic; revision: RevisionPublic }) => void
  onRouteUpdate?: (route: RoutePublic) => void
}

export function RouteCollabPanel({
  routeId,
  route,
  routeWaypoints = [],
  routeMedia = [],
  routeCreatorId,
  routeCreatorName,
  routeRegion,
  routeCreatedAt,
  etag,
  token,
  onRevert,
  onRouteUpdate,
}: RouteCollabPanelProps) {
  const auth = useAuth()
  const { t, locale } = useLocale()
  const [revisions, setRevisions] = useState<EnrichedRevision[] | null>(null)
  const [mediaEvents, setMediaEvents] = useState<EnrichedMedia[]>([])
  const [proposals, setProposals] = useState<EnrichedProposal[]>([])
  const [error, setError] = useState<string | null>(null)
  const [publicationStatus, setPublicationStatus] = useState<string | null>(null)
  const [openId, setOpenId] = useState<string | null>(null)
  const [snapshots, setSnapshots] = useState<SnapshotCache>({})
  const [reverting, setReverting] = useState(false)
  const [proposalActingId, setProposalActingId] = useState<string | null>(null)
  const [publicationActing, setPublicationActing] = useState<'approve' | 'reject' | null>(null)
  const requestSeq = useRef(0)
  const publicationOperationSeq = useRef(0)
  const canRevert =
    auth.status === 'authed' &&
    (auth.user?.role === 'admin' || (routeCreatorId != null && auth.user?.id === routeCreatorId))
  const canModerateProposals =
    auth.status === 'authed' &&
    auth.user != null &&
    routeCreatorId != null &&
    canModerateRouteProposals(auth.user, {
      created_by: routeCreatorId,
      region: routeRegion,
    })
  const canReviewPublication =
    auth.status === 'authed' &&
    auth.user != null &&
    route != null &&
    canApprovePublishRoute(auth.user, route)
  const isOwnPublicationNomination =
    auth.status === 'authed' && route != null && auth.user?.id === route.created_by

  useEffect(() => {
    const requestId = ++requestSeq.current
    let cancelled = false
    setError(null)
    setRevisions(null)
    setMediaEvents([])
    setProposals([])
    setPublicationStatus(null)
    setPublicationActing(null)
    publicationOperationSeq.current += 1
    setOpenId(null)
    setSnapshots({})
    loadPanelData(routeId, token)
      .then(({ items, media, routeProposals }) => {
        if (cancelled || requestId !== requestSeq.current) return
        setError(null)
        setRevisions(items)
        setMediaEvents(media)
        setProposals(routeProposals)
      })
      .catch((err) => {
        if (cancelled || requestId !== requestSeq.current) return
        setError(friendlyError(err, t))
      })
    return () => {
      cancelled = true
      requestSeq.current += 1
      publicationOperationSeq.current += 1
    }
  }, [routeId, t, token, auth.user?.id, etag])

  function isCurrentRequest(requestId: number): boolean {
    return requestId === requestSeq.current
  }

  async function handleRevert(rev: RevisionPublic) {
    if (!auth.token || !etag) return
    const msg = locale === 'zh' ? `确定回退到 r${rev.rev_index}？` : `Revert to r${rev.rev_index}?`
    if (!confirm(msg)) return
    const requestId = requestSeq.current
    setReverting(true)
    try {
      const result = await api.revertRoute(routeId, rev.id, auth.token, etag)
      if (!isCurrentRequest(requestId)) return
      onRevert?.(result)
      const items = await api.listRevisions(routeId, auth.token)
      if (!isCurrentRequest(requestId)) return
      setRevisions(items)
    } catch (err) {
      if (!isCurrentRequest(requestId)) return
      setError(friendlyError(err, t))
    } finally {
      if (isCurrentRequest(requestId)) setReverting(false)
    }
  }

  async function reloadPanel(requestId = requestSeq.current) {
    setError(null)
    const next = await loadPanelData(routeId, token)
    if (!isCurrentRequest(requestId)) return
    setError(null)
    setRevisions(next.items)
    setMediaEvents(next.media)
    setProposals(next.routeProposals)
  }

  async function handleApproveProposal(proposal: ProposalPublic) {
    if (!auth.token) return
    const requestId = requestSeq.current
    setProposalActingId(proposal.id)
    try {
      const result = await api.approveProposal(proposal.id, auth.token)
      if (!isCurrentRequest(requestId)) return
      onRouteUpdate?.(result.route)
      await reloadPanel(requestId)
    } catch (err) {
      if (!isCurrentRequest(requestId)) return
      setError(friendlyError(err, t))
    } finally {
      if (isCurrentRequest(requestId)) setProposalActingId(null)
    }
  }

  async function handleRejectProposal(proposal: ProposalPublic) {
    if (!auth.token) return
    const note = prompt(locale === 'zh' ? '拒绝理由（可选）' : 'Optional rejection note')
    if (note === null) return
    const requestId = requestSeq.current
    setProposalActingId(proposal.id)
    try {
      await api.rejectProposal(proposal.id, auth.token, note.trim().length > 0 ? { note } : {})
      if (!isCurrentRequest(requestId)) return
      await reloadPanel(requestId)
    } catch (err) {
      if (!isCurrentRequest(requestId)) return
      setError(friendlyError(err, t))
    } finally {
      if (isCurrentRequest(requestId)) setProposalActingId(null)
    }
  }

  async function handleWithdrawProposal(proposal: ProposalPublic) {
    if (!auth.token) return
    const requestId = requestSeq.current
    setProposalActingId(proposal.id)
    try {
      await api.withdrawProposal(proposal.id, auth.token)
      if (!isCurrentRequest(requestId)) return
      await reloadPanel(requestId)
    } catch (err) {
      if (!isCurrentRequest(requestId)) return
      setError(friendlyError(err, t))
    } finally {
      if (isCurrentRequest(requestId)) setProposalActingId(null)
    }
  }

  async function handlePublicationReview(action: 'approve' | 'reject') {
    if (!auth.token || !route || !etag || publicationActing) return
    const confirmed = confirm(
      action === 'approve'
        ? t('routeCollab.publication.approveConfirm')
        : t('routeCollab.publication.rejectConfirm'),
    )
    if (!confirmed) return
    const requestId = requestSeq.current
    const operationId = ++publicationOperationSeq.current
    const reviewedRouteId = route.id
    const reviewedEtag = etag
    const reviewedToken = auth.token
    setPublicationActing(action)
    setPublicationStatus(null)
    setError(null)
    try {
      const result =
        action === 'approve'
          ? await api.approveRoutePublish(reviewedRouteId, reviewedToken, reviewedEtag)
          : await api.rejectRoutePublish(reviewedRouteId, reviewedToken, reviewedEtag)
      if (!isCurrentRequest(requestId) || result.route.id !== reviewedRouteId) return
      onRouteUpdate?.(result.route)
      const message =
        action === 'approve'
          ? t('routeCollab.publication.approved')
          : t('routeCollab.publication.rejected')
      setPublicationStatus(message)
      await reloadPanel(requestId)
    } catch (err) {
      if (!isCurrentRequest(requestId)) return
      const message =
        err instanceof ApiError && (err.status === 409 || err.status === 412)
          ? t('routeCollab.publication.conflict')
          : friendlyError(err, t)
      setError(message)
    } finally {
      if (isCurrentRequest(requestId) && publicationOperationSeq.current === operationId) {
        setPublicationActing(null)
      }
    }
  }

  const timeline = useMemo(() => {
    if (!revisions) return []
    const previousByRevisionId = new Map<string, RevisionPublic | null>()
    for (let i = 0; i < revisions.length; i += 1) {
      previousByRevisionId.set(revisions[i]!.id, revisions[i + 1] ?? null)
    }

    const entries: Array<
      | { kind: 'proposal'; id: string; at: string; proposal: EnrichedProposal }
      | { kind: 'media'; id: string; at: string; media: EnrichedMedia }
      | {
          kind: 'revision'
          id: string
          at: string
          revision: EnrichedRevision
          previous: EnrichedRevision | null
        }
      | { kind: 'created'; id: string; at: string }
    > = []

    for (const proposal of proposals) {
      entries.push({
        kind: 'proposal',
        id: `proposal-${proposal.id}`,
        at: proposal.resolved_at ?? proposal.created_at,
        proposal,
      })
    }
    for (const item of mediaEvents) {
      entries.push({
        kind: 'media',
        id: `media-${item.id}`,
        at: item.created_at,
        media: item,
      })
    }
    for (const rev of revisions) {
      entries.push({
        kind: 'revision',
        id: `revision-${rev.id}`,
        at: rev.created_at,
        revision: rev,
        previous: previousByRevisionId.get(rev.id) ?? null,
      })
    }
    if (routeCreatedAt) {
      entries.push({ kind: 'created', id: 'route-created', at: routeCreatedAt })
    }

    entries.sort((a, b) => toMillis(b.at) - toMillis(a.at))
    return entries
  }, [revisions, proposals, mediaEvents, routeCreatedAt])

  function toggle(revId: string, previousRevId?: string) {
    setOpenId((prev) => (prev === revId ? null : revId))
    const requestId = requestSeq.current
    if (!snapshots[revId]) {
      setSnapshots((prev) => ({ ...prev, [revId]: 'loading' }))
      api
        .getRevisionSnapshot(routeId, revId, token)
        .then(({ snapshot }) => {
          if (requestId !== requestSeq.current) return
          setSnapshots((prev) => ({ ...prev, [revId]: snapshot }))
        })
        .catch(() => {
          if (requestId !== requestSeq.current) return
          setSnapshots((prev) => ({ ...prev, [revId]: 'error' }))
        })
    }
    if (previousRevId && !snapshots[previousRevId]) {
      setSnapshots((prev) => ({ ...prev, [previousRevId]: 'loading' }))
      api
        .getRevisionSnapshot(routeId, previousRevId, token)
        .then(({ snapshot }) => {
          if (requestId !== requestSeq.current) return
          setSnapshots((prev) => ({ ...prev, [previousRevId]: snapshot }))
        })
        .catch(() => {
          if (requestId !== requestSeq.current) return
          setSnapshots((prev) => ({ ...prev, [previousRevId]: 'error' }))
        })
    }
  }

  if (error) {
    return <p className="text-xs text-[var(--xr-danger)]">{error}</p>
  }
  if (!revisions) {
    return <p className="text-xs text-[var(--xr-text-muted)]">…</p>
  }

  return (
    <div className="flex flex-col gap-2" data-testid="collab-panel">
      <section
        className="rounded-[var(--xr-radius-sm)] border border-dashed border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] px-3 py-2"
        data-testid="comments-unavailable"
      >
        <p className="text-xs font-medium text-[var(--xr-text-primary)]">
          {t('routeCollab.commentsUnavailable.title')}
        </p>
        <p className="mt-1 text-[11px] text-[var(--xr-text-muted)]">
          {t('routeCollab.commentsUnavailable.body')}
        </p>
      </section>
      <p className="text-xs text-[var(--xr-text-muted)]">
        {locale === 'zh'
          ? `共 ${timeline.length} 条协作记录（${revisions.length} 次修订）`
          : `${timeline.length} collaboration events (${revisions.length} revisions)`}
      </p>
      {publicationStatus ? (
        <p className="text-xs text-[var(--xr-success)]" data-testid="publication-review-status">
          {publicationStatus}
        </p>
      ) : null}
      {route?.status === 'pending_review' && (
        <PublicationReviewCard
          route={route}
          etag={etag ?? route.etag}
          authorName={routeCreatorName}
          canReview={canReviewPublication}
          isOwnNomination={isOwnPublicationNomination}
          acting={publicationActing}
          t={t}
          onApprove={() => void handlePublicationReview('approve')}
          onReject={() => void handlePublicationReview('reject')}
        />
      )}
      {timeline.length === 0 ? (
        <p className="text-xs text-[var(--xr-text-muted)]">
          {locale === 'zh' ? '尚无修订。每次发布都会留下一条记录。' : 'No revisions yet.'}
        </p>
      ) : (
        <ol className="flex flex-col gap-2" data-testid="route-history-panel">
          {timeline.map((entry) => {
            if (entry.kind === 'proposal') {
              return (
                <ProposalRow
                  key={entry.id}
                  proposal={entry.proposal}
                  locale={locale}
                  route={route}
                  routeWaypoints={routeWaypoints}
                  routeMedia={routeMedia}
                  t={t}
                  acting={proposalActingId === entry.proposal.id}
                  isAuthor={auth.user?.id === entry.proposal.author_id}
                  canModerate={canModerateProposals}
                  onApprove={() => void handleApproveProposal(entry.proposal)}
                  onReject={() => void handleRejectProposal(entry.proposal)}
                  onWithdraw={() => void handleWithdrawProposal(entry.proposal)}
                />
              )
            }
            if (entry.kind === 'media') {
              return (
                <MediaEventRow key={entry.id} item={entry.media} locale={locale} token={token} />
              )
            }
            if (entry.kind === 'revision') {
              return (
                <RevisionRow
                  key={entry.id}
                  rev={entry.revision}
                  previous={entry.previous}
                  isOpen={openId === entry.revision.id}
                  snapshot={snapshots[entry.revision.id] ?? null}
                  previousSnapshot={entry.previous ? (snapshots[entry.previous.id] ?? null) : null}
                  onToggle={() => toggle(entry.revision.id, entry.previous?.id)}
                  onRevert={canRevert ? () => void handleRevert(entry.revision) : undefined}
                  reverting={reverting}
                  routeCreatorId={routeCreatorId}
                  routeCreatorName={routeCreatorName}
                  t={t}
                  locale={locale}
                />
              )
            }
            return (
              <CreatedRouteRow
                key={entry.id}
                routeCreatorId={routeCreatorId}
                routeCreatorName={routeCreatorName}
                routeCreatedAt={entry.at}
                locale={locale}
              />
            )
          })}
        </ol>
      )}
    </div>
  )
}

function PublicationReviewCard({
  route,
  etag,
  authorName,
  canReview,
  isOwnNomination,
  acting,
  t,
  onApprove,
  onReject,
}: {
  route: RoutePublic
  etag: string
  authorName?: string
  canReview: boolean
  isOwnNomination: boolean
  acting: 'approve' | 'reject' | null
  t: ReturnType<typeof useLocale>['t']
  onApprove: () => void
  onReject: () => void
}) {
  const author = authorName?.trim() || route.created_by.slice(0, 8) + '…'
  const region = formatRegion(route.region)
  return (
    <section
      className="rounded-[var(--xr-radius-sm)] border border-[var(--xr-brand-primary)]/40 bg-[var(--xr-brand-primary-soft)] px-3 py-2"
      data-testid="publication-review-card"
      aria-label={t('routeCollab.publication.title')}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-semibold text-[var(--xr-text-primary)]">
            {t('routeCollab.publication.title')}
          </p>
          <p className="mt-0.5 text-[11px] text-[var(--xr-text-secondary)]">
            {t('routeCollab.publication.author').replace('{{author}}', author)}
          </p>
        </div>
        <span
          role="status"
          className="rounded-full bg-[var(--xr-bg-surface)] px-2 py-0.5 text-[10px] font-medium text-[var(--xr-text-secondary)]"
        >
          {t('routeCollab.publication.status')}
        </span>
      </div>
      <div className="mt-2 space-y-1 text-[11px] text-[var(--xr-text-secondary)]">
        <div
          className="rounded-[var(--xr-radius-sm)] bg-[var(--xr-bg-surface)]/70 px-2 py-1.5"
          data-testid="publication-review-route-context"
        >
          <p className="truncate text-xs font-medium text-[var(--xr-text-primary)]">
            {route.title}
          </p>
          {route.summary ? (
            <p className="mt-0.5 line-clamp-2 break-words text-[11px] text-[var(--xr-text-secondary)]">
              {route.summary}
            </p>
          ) : null}
        </div>
        <p>{t('routeCollab.publication.body')}</p>
        <p>{t('routeCollab.publication.noEdit')}</p>
        {region ? <p>{t('routeCollab.publication.region').replace('{{region}}', region)}</p> : null}
        <details className="group rounded-[var(--xr-radius-sm)] bg-[var(--xr-bg-surface)]/70 px-2 py-1">
          <summary className="cursor-pointer select-none text-[10px] font-medium text-[var(--xr-text-muted)]">
            {t('routeCollab.publication.submittedVersion')}
          </summary>
          <code
            className="mt-1 block max-w-full whitespace-normal break-all font-mono text-[10px] leading-relaxed text-[var(--xr-text-secondary)]"
            data-testid="publication-review-etag"
          >
            {etag}
          </code>
        </details>
      </div>
      {isOwnNomination ? (
        <p className="mt-2 text-[11px] text-[var(--xr-text-muted)]">
          {t('routeCollab.publication.selfBlocked')}
        </p>
      ) : null}
      {canReview && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          <button
            type="button"
            onClick={onApprove}
            disabled={acting !== null}
            className="rounded-[var(--xr-radius-sm)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] px-2 py-1 text-[10px] font-medium text-[var(--xr-text-secondary)] hover:bg-[var(--xr-bg-surface-elev)] disabled:opacity-50"
            data-testid="publication-review-approve"
          >
            {t('routeCollab.publication.approve')}
          </button>
          <button
            type="button"
            onClick={onReject}
            disabled={acting !== null}
            className="rounded-[var(--xr-radius-sm)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] px-2 py-1 text-[10px] font-medium text-[var(--xr-text-secondary)] hover:bg-[var(--xr-bg-surface-elev)] disabled:opacity-50"
            data-testid="publication-review-reject"
          >
            {t('routeCollab.publication.reject')}
          </button>
        </div>
      )}
    </section>
  )
}

function ProposalRow({
  proposal,
  locale,
  route,
  routeWaypoints,
  routeMedia,
  t,
  isAuthor,
  canModerate,
  acting,
  onApprove,
  onReject,
  onWithdraw,
}: {
  proposal: EnrichedProposal
  locale: 'zh' | 'en'
  route?: RoutePublic
  routeWaypoints: readonly Waypoint[]
  routeMedia: readonly MediaAssetPublic[]
  t: ReturnType<typeof useLocale>['t']
  isAuthor: boolean
  canModerate: boolean
  acting: boolean
  onApprove: () => void
  onReject: () => void
  onWithdraw: () => void
}) {
  const authorDisplay = proposal.author_name ?? proposal.author_id.slice(0, 8) + '…'
  const proposalDiff = useMemo(
    () => computeProposalDiff(proposal, route, routeWaypoints, routeMedia),
    [proposal, route, routeWaypoints, routeMedia],
  )
  const changes = proposalChangesRecord(proposal.changes)
  return (
    <li
      className="rounded-[var(--xr-radius-sm)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] px-3 py-2"
      data-testid="proposal-row"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-medium text-[var(--xr-text-primary)]">
            {locale === 'zh' ? '建议编辑' : 'Proposal'} · {authorDisplay}
          </p>
          <p className="mt-0.5 text-[11px] text-[var(--xr-text-muted)]">
            {proposal.message && `${proposal.message} · `}
            {formatRelative(proposal.resolved_at ?? proposal.created_at, locale)}
          </p>
        </div>
        <span
          role="status"
          className="rounded-full bg-[var(--xr-bg-surface-elev)] px-2 py-0.5 text-[10px] font-medium text-[var(--xr-text-secondary)]"
        >
          {proposalStatusLabel(proposal.status, locale)}
        </span>
      </div>
      {proposal.status === 'rejected' && proposal.review_note?.trim() ? (
        <p
          className="mt-2 rounded-[var(--xr-radius-sm)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface-elev)] px-2 py-1 text-[11px] text-[var(--xr-text-secondary)]"
          data-testid="proposal-rejection-note"
        >
          <span className="font-medium text-[var(--xr-text-primary)]">
            {locale === 'zh' ? '拒绝理由：' : 'Rejection note: '}
          </span>
          {proposal.review_note}
        </p>
      ) : null}
      {changes ? (
        <div
          className="mt-2 rounded-[var(--xr-radius-sm)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface-elev)] px-2 py-2"
          data-testid="proposal-change-preview"
        >
          <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-[var(--xr-text-muted)]">
            {locale === 'zh' ? '改动预览' : 'Change preview'}
          </p>
          {route && proposalDiff.length > 0 ? (
            <DiffList diff={proposalDiff} locale={locale} parentEtag={proposal.base_etag} t={t} />
          ) : (
            <ProposalChangeSummary changes={changes} locale={locale} />
          )}
        </div>
      ) : null}
      {proposal.status === 'open' && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {canModerate && !isAuthor && (
            <>
              <button
                type="button"
                onClick={onApprove}
                disabled={acting}
                className="rounded-[var(--xr-radius-sm)] border border-[var(--xr-border-default)] px-2 py-1 text-[10px] font-medium text-[var(--xr-text-secondary)] hover:bg-[var(--xr-bg-surface-elev)] disabled:opacity-50"
              >
                {locale === 'zh' ? '批准' : 'Approve'}
              </button>
              <button
                type="button"
                onClick={onReject}
                disabled={acting}
                className="rounded-[var(--xr-radius-sm)] border border-[var(--xr-border-default)] px-2 py-1 text-[10px] font-medium text-[var(--xr-text-secondary)] hover:bg-[var(--xr-bg-surface-elev)] disabled:opacity-50"
              >
                {locale === 'zh' ? '拒绝' : 'Reject'}
              </button>
            </>
          )}
          {isAuthor && (
            <button
              type="button"
              onClick={onWithdraw}
              disabled={acting}
              className="rounded-[var(--xr-radius-sm)] border border-[var(--xr-border-default)] px-2 py-1 text-[10px] font-medium text-[var(--xr-text-secondary)] hover:bg-[var(--xr-bg-surface-elev)] disabled:opacity-50"
            >
              {locale === 'zh' ? '撤回' : 'Withdraw'}
            </button>
          )}
        </div>
      )}
    </li>
  )
}

function MediaEventRow({
  item,
  locale,
  token,
}: {
  item: EnrichedMedia
  locale: 'zh' | 'en'
  token?: string | null
}) {
  const uploader = item.uploaded_by_name ?? (locale === 'zh' ? '未知用户' : 'Unknown user')
  const kindLabel =
    item.kind === 'video'
      ? locale === 'zh'
        ? '视频'
        : 'video'
      : locale === 'zh'
        ? '图片'
        : 'image'
  return (
    <li className="flex items-center gap-2 rounded-[var(--xr-radius-sm)] border border-[var(--xr-border-default)] px-3 py-2">
      <span className="flex h-6 w-6 flex-none items-center justify-center overflow-hidden rounded bg-[var(--xr-bg-surface-elev)] text-[10px] font-semibold text-[var(--xr-text-muted)]">
        {item.kind === 'image' ? (
          <AuthorizedMediaImage
            source={item.thumb_url ?? item.blob_url}
            token={token}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          'VID'
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-[var(--xr-text-primary)]">
          {uploader}{' '}
          <span className="font-normal text-[var(--xr-text-secondary)]">
            {locale === 'zh' ? `上传了${kindLabel}` : `uploaded ${kindLabel}`}
          </span>
        </span>
        <span className="block text-[11px] text-[var(--xr-text-muted)]">
          {formatRelative(item.created_at, locale)}
        </span>
      </span>
    </li>
  )
}

function CreatedRouteRow({
  routeCreatorId,
  routeCreatorName,
  routeCreatedAt,
  locale,
}: {
  routeCreatorId?: string
  routeCreatorName?: string
  routeCreatedAt: string
  locale: 'zh' | 'en'
}) {
  return (
    <li className="flex items-center gap-2 rounded-[var(--xr-radius-sm)] border border-dashed border-[var(--xr-border-default)] px-3 py-2">
      <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-[var(--xr-accent-soft)] text-[10px] font-semibold text-[var(--xr-accent)]">
        +
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-[var(--xr-text-primary)]">
          {routeCreatorName ?? routeCreatorId?.slice(0, 8) + '…'}{' '}
          <span className="font-normal text-[var(--xr-text-secondary)]">
            {locale === 'zh' ? '创建了线路' : 'created the route'}
          </span>
        </span>
        <span className="block text-[11px] text-[var(--xr-text-muted)]">
          {formatRelative(routeCreatedAt, locale)}
        </span>
      </span>
    </li>
  )
}

interface RevisionRowProps {
  rev: EnrichedRevision
  previous: EnrichedRevision | null
  isOpen: boolean
  snapshot: Record<string, unknown> | 'loading' | 'error' | null
  previousSnapshot: Record<string, unknown> | 'loading' | 'error' | null
  onToggle: () => void
  onRevert?: () => void
  reverting?: boolean
  routeCreatorId?: string
  routeCreatorName?: string
  t: ReturnType<typeof useLocale>['t']
  locale: 'zh' | 'en'
}

function RevisionRow({
  rev,
  previous,
  isOpen,
  snapshot,
  previousSnapshot,
  onToggle,
  onRevert,
  reverting,
  routeCreatorId,
  routeCreatorName,
  t,
  locale,
}: RevisionRowProps) {
  const snapshotRecord = snapshotToRecord(snapshot)
  const previousSnapshotRecord = previous ? snapshotToRecord(previousSnapshot) : {}
  const snapshotLoading =
    snapshot === 'loading' || (previous != null && previousSnapshot === 'loading')
  const snapshotError = snapshot === 'error' || (previous != null && previousSnapshot === 'error')
  const diff = useMemo(
    () =>
      snapshotRecord && previousSnapshotRecord
        ? computeDiff(normalizeSnapshot(previousSnapshotRecord), normalizeSnapshot(snapshotRecord))
        : [],
    [snapshotRecord, previousSnapshotRecord],
  )

  const when = formatRelative(rev.created_at, locale)
  const editorDisplay = rev.editor_name
    ? rev.editor_name
    : rev.editor_id === routeCreatorId && routeCreatorName
      ? routeCreatorName
      : rev.editor_id.slice(0, 8) + '…'

  return (
    <li
      data-testid="revision-row"
      className="rounded-[var(--xr-radius-sm)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)]"
    >
      <div className="flex items-center gap-2 px-3 py-2 transition hover:bg-[var(--xr-bg-surface-elev)]">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isOpen}
          className="flex min-w-0 flex-1 items-center gap-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--xr-brand-primary)]"
        >
          <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-[var(--xr-brand-primary-soft)] text-[10px] font-semibold text-[var(--xr-brand-primary)]">
            r{rev.rev_index}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-xs font-medium text-[var(--xr-text-primary)]">
              {editorDisplay}
            </span>
            <span className="block text-[11px] text-[var(--xr-text-muted)]">
              {when}
              {rev.message ? ` · ${rev.message}` : ''}
            </span>
          </span>
          {rev.conflicts.length > 0 && (
            <span className="flex-none rounded-full bg-[var(--xr-warning-soft)] px-2 py-0.5 text-[10px] font-medium text-[var(--xr-warning)]">
              {locale === 'zh'
                ? `${rev.conflicts.length} 冲突`
                : `${rev.conflicts.length} conflicts`}
            </span>
          )}
          <span aria-hidden="true" className="flex-none text-[var(--xr-text-muted)]">
            {isOpen ? '▾' : '▸'}
          </span>
        </button>
        {onRevert && (
          <button
            type="button"
            onClick={onRevert}
            disabled={reverting}
            className="flex-none rounded-[var(--xr-radius-sm)] border border-[var(--xr-border-default)] px-2 py-1 text-[10px] font-medium text-[var(--xr-text-secondary)] hover:bg-[var(--xr-bg-surface)] disabled:opacity-50"
            data-testid="revision-revert"
          >
            {locale === 'zh' ? '回退' : 'revert'}
          </button>
        )}
      </div>
      {isOpen && (
        <div className="border-t border-[var(--xr-border-default)] px-3 py-2">
          {snapshotLoading && <p className="text-xs text-[var(--xr-text-muted)]">…</p>}
          {snapshotError && (
            <p className="text-xs text-[var(--xr-danger)]">
              {locale === 'zh' ? '加载快照失败' : 'Failed to load snapshot'}
            </p>
          )}
          {!snapshotLoading && !snapshotError && snapshotRecord && previousSnapshotRecord && (
            <DiffList diff={diff} locale={locale} parentEtag={rev.parent_etag} t={t} />
          )}
        </div>
      )}
    </li>
  )
}

type FieldDiff =
  | { kind: 'field'; field: DiffField; before: string; after: string }
  | { kind: 'geometry'; waypoints: WaypointChange[]; trackChanged: boolean }
  | { kind: 'media'; added: MediaRef[]; removed: MediaRef[] }

type WaypointKindKey = NonNullable<Waypoint['kind']>

interface WaypointChange {
  type: 'added' | 'removed' | 'modified'
  id: string
  kindKey?: WaypointKindKey
  before?: string
  after?: string
}

interface MediaRef {
  id: string
  kindKey?: string
}

const PROPOSAL_METADATA_FIELDS = new Set<string>([
  'title',
  'summary',
  'activities',
  'region',
  'natural_features',
  'difficulty',
  'route_type',
  'seasonality',
])

function snapshotToRecord(s: SnapshotCache[string] | null): Record<string, unknown> | null {
  if (!s || s === 'loading' || s === 'error') return null
  return s
}

function normalizeSnapshot(raw: Record<string, unknown>): NormalizedSnapshot {
  if (Object.keys(raw).length === 0) return { data: {} }
  const metadata = raw.route_metadata
  const data: Record<string, unknown> =
    metadata && typeof metadata === 'object' && !Array.isArray(metadata)
      ? { ...(metadata as Record<string, unknown>) }
      : { ...raw }
  if ('route_description_md' in raw) data.route_description_md = raw.route_description_md
  if ('route_geometry' in raw) data.route_geometry = raw.route_geometry
  if ('route_media' in raw) data.route_media = raw.route_media
  return { data }
}

function computeDiff(before: NormalizedSnapshot, after: NormalizedSnapshot): FieldDiff[] {
  const out: FieldDiff[] = []

  // Plain metadata + description fields, excluding noise and the structured
  // fields rendered on their own below.
  const fieldSet = new Set<string>([
    ...METADATA_FIELD_ORDER,
    ...Object.keys(before.data),
    ...Object.keys(after.data),
  ])
  const orderedFields = [...fieldSet]
    .filter((f) => !NOISE_DIFF_FIELDS.has(f) && !STRUCTURED_DIFF_FIELDS.has(f))
    .sort((a, b) => {
      const ai = METADATA_FIELD_ORDER.indexOf(a)
      const bi = METADATA_FIELD_ORDER.indexOf(b)
      if (ai >= 0 && bi >= 0) return ai - bi
      if (ai >= 0) return -1
      if (bi >= 0) return 1
      return a.localeCompare(b)
    })
  for (const field of orderedFields) {
    const a = stringify(before.data[field])
    const b = stringify(after.data[field])
    if (a !== b) out.push({ kind: 'field', field, before: a, after: b })
  }

  const geometry = computeGeometryDiff(before.data.route_geometry, after.data.route_geometry)
  if (geometry) out.push(geometry)

  const media = computeMediaDiff(before.data.route_media, after.data.route_media)
  if (media) out.push(media)

  return out
}

function computeProposalDiff(
  proposal: ProposalPublic,
  route: RoutePublic | undefined,
  routeWaypoints: readonly Waypoint[],
  routeMedia: readonly MediaAssetPublic[],
): FieldDiff[] {
  const changes = proposalChangesRecord(proposal.changes)
  if (!changes || !route) return []

  const before = normalizeSnapshot(currentRouteSnapshot(route, routeWaypoints, routeMedia))
  const afterData: Record<string, unknown> = { ...before.data }

  const metadataPatch = proposalMetadataPatch(changes)
  for (const [key, value] of Object.entries(metadataPatch)) {
    afterData[key] = value
  }

  const beforeGeometry = recordValue(before.data.route_geometry) ?? {}
  let afterGeometry: Record<string, unknown> | null = null
  if (Array.isArray(changes.waypoints)) {
    afterGeometry = { ...beforeGeometry, waypoints: changes.waypoints }
  }
  if (typeof changes.geometry_staged_path === 'string') {
    afterGeometry = {
      ...(afterGeometry ?? beforeGeometry),
      gpx_sha256: `staged:${changes.geometry_staged_path}`,
    }
  }
  if (afterGeometry) afterData.route_geometry = afterGeometry

  const mediaAdditions = proposalMediaAdditions(changes)
  if (mediaAdditions.length > 0) {
    afterData.route_media = [...proposalMediaRefs(routeMedia), ...mediaAdditions]
  }

  return computeDiff(before, { data: afterData })
}

function currentRouteSnapshot(
  route: RoutePublic,
  routeWaypoints: readonly Waypoint[],
  routeMedia: readonly MediaAssetPublic[],
): Record<string, unknown> {
  return {
    route_metadata: route,
    route_geometry: {
      gpx_sha256: route.geometry_url ?? null,
      waypoints: routeWaypoints,
    },
    route_media: proposalMediaRefs(routeMedia),
  }
}

function proposalChangesRecord(changes: unknown): Record<string, unknown> | null {
  const record = recordValue(changes)
  return record && Object.keys(record).length > 0 ? record : null
}

function recordValue(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function proposalMetadataPatch(changes: Record<string, unknown>): Record<string, unknown> {
  const nested = recordValue(changes.metadata_patch)
  if (nested) return nested
  return Object.fromEntries(
    Object.entries(changes).filter(([key]) => PROPOSAL_METADATA_FIELDS.has(key)),
  )
}

function proposalMediaRefs(media: readonly MediaAssetPublic[]): MediaRef[] {
  return media.map((item) => ({ id: item.id, kindKey: item.kind }))
}

function proposalMediaAdditions(changes: Record<string, unknown>): MediaRef[] {
  if (!Array.isArray(changes.media_additions)) return []
  return changes.media_additions.map((item, index) => {
    const record = recordValue(item) ?? {}
    const id =
      typeof record.id === 'string'
        ? record.id
        : typeof record.blob_path === 'string'
          ? record.blob_path
          : `staged-media-${index}`
    return {
      id,
      kindKey: typeof record.kind === 'string' ? record.kind : undefined,
    }
  })
}

function computeGeometryDiff(
  beforeGeo: unknown,
  afterGeo: unknown,
): Extract<FieldDiff, { kind: 'geometry' }> | null {
  const before = geometryWaypoints(beforeGeo)
  const after = geometryWaypoints(afterGeo)
  const trackChanged = geometryTrackSig(beforeGeo) !== geometryTrackSig(afterGeo)
  const waypoints: WaypointChange[] = []
  for (const [id, wp] of after) {
    const prev = before.get(id)
    if (!prev) {
      waypoints.push({ type: 'added', id, kindKey: waypointKind(wp), after: waypointText(wp) })
    } else if (waypointSignature(prev) !== waypointSignature(wp)) {
      waypoints.push({
        type: 'modified',
        id,
        kindKey: waypointKind(wp),
        before: waypointText(prev),
        after: waypointText(wp),
      })
    }
  }
  for (const [id, wp] of before) {
    if (!after.has(id)) {
      waypoints.push({ type: 'removed', id, kindKey: waypointKind(wp), before: waypointText(wp) })
    }
  }
  if (waypoints.length === 0 && !trackChanged) return null
  return { kind: 'geometry', waypoints, trackChanged }
}

function computeMediaDiff(
  beforeMedia: unknown,
  afterMedia: unknown,
): Extract<FieldDiff, { kind: 'media' }> | null {
  const before = mediaMap(beforeMedia)
  const after = mediaMap(afterMedia)
  const added: MediaRef[] = []
  const removed: MediaRef[] = []
  for (const [id, m] of after) {
    if (!before.has(id)) added.push({ id, kindKey: mediaKind(m) })
  }
  for (const [id, m] of before) {
    if (!after.has(id)) removed.push({ id, kindKey: mediaKind(m) })
  }
  if (added.length === 0 && removed.length === 0) return null
  return { kind: 'media', added, removed }
}

function geometryWaypoints(geo: unknown): Map<string, Record<string, unknown>> {
  const map = new Map<string, Record<string, unknown>>()
  if (geo && typeof geo === 'object' && !Array.isArray(geo)) {
    const g = geo as Record<string, unknown>
    const list = Array.isArray(g.waypoints) ? g.waypoints : Array.isArray(g.points) ? g.points : []
    for (const wp of list) {
      if (wp && typeof wp === 'object' && 'id' in wp) {
        map.set(String((wp as Record<string, unknown>).id), wp as Record<string, unknown>)
      }
    }
  }
  return map
}

function geometryTrackSig(geo: unknown): string | null {
  if (geo && typeof geo === 'object' && !Array.isArray(geo)) {
    const g = geo as Record<string, unknown>
    if (typeof g.gpx_sha256 === 'string') return g.gpx_sha256
    if (typeof g.track_point_count === 'number') return `tpc:${g.track_point_count}`
  }
  return null
}

function waypointSignature(wp: Record<string, unknown>): string {
  return JSON.stringify([
    wp.kind ?? null,
    wp.name ?? null,
    wp.desc ?? null,
    wp.lat ?? null,
    wp.lon ?? null,
    wp.ele ?? null,
  ])
}

function waypointText(wp: Record<string, unknown>): string {
  const name = typeof wp.name === 'string' ? wp.name.trim() : ''
  const desc = typeof wp.desc === 'string' ? wp.desc.trim() : ''
  return name || desc || String(wp.id ?? '').slice(0, 8)
}

function waypointKind(wp: Record<string, unknown>): WaypointKindKey | undefined {
  return typeof wp.kind === 'string' ? (wp.kind as WaypointKindKey) : undefined
}

function mediaMap(value: unknown): Map<string, Record<string, unknown>> {
  const map = new Map<string, Record<string, unknown>>()
  if (Array.isArray(value)) {
    for (const m of value) {
      if (m && typeof m === 'object' && 'id' in m) {
        map.set(String((m as Record<string, unknown>).id), m as Record<string, unknown>)
      }
    }
  }
  return map
}

function mediaKind(m: Record<string, unknown>): string | undefined {
  return typeof m.kind === 'string' ? m.kind : undefined
}

function stringify(v: unknown): string {
  if (v === undefined || v === null) return ''
  if (typeof v === 'string') return v
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  return JSON.stringify(v)
}

function DiffList({
  diff,
  locale,
  parentEtag,
  t,
}: {
  diff: FieldDiff[]
  locale: 'zh' | 'en'
  parentEtag: string
  t: ReturnType<typeof useLocale>['t']
}) {
  if (diff.length === 0) {
    return (
      <p className="text-xs text-[var(--xr-text-muted)]">
        {locale === 'zh'
          ? `较上一版无字段变更（parent ${parentEtag.slice(0, 8)}…）`
          : `No field changes vs parent (${parentEtag.slice(0, 8)}…)`}
      </p>
    )
  }
  return (
    <dl className="flex flex-col gap-2 text-xs" data-testid="revision-diff">
      {diff.map((entry) => {
        if (entry.kind === 'geometry') {
          return <GeometryDiffEntry key="__geometry" entry={entry} locale={locale} t={t} />
        }
        if (entry.kind === 'media') {
          return <MediaDiffEntry key="__media" entry={entry} locale={locale} />
        }
        return <FieldDiffEntry key={entry.field} entry={entry} />
      })}
    </dl>
  )
}

function ProposalChangeSummary({
  changes,
  locale,
}: {
  changes: Record<string, unknown>
  locale: 'zh' | 'en'
}) {
  const metadataPatch = proposalMetadataPatch(changes)
  const rows: string[] = []
  for (const [key, value] of Object.entries(metadataPatch)) {
    rows.push(`${key}: ${stringify(value) || (locale === 'zh' ? '清空' : 'cleared')}`)
  }
  if (Array.isArray(changes.waypoints)) {
    rows.push(
      locale === 'zh'
        ? `替换路点：${changes.waypoints.length} 个`
        : `Replace waypoints: ${changes.waypoints.length}`,
    )
  }
  if (typeof changes.geometry_staged_path === 'string') {
    rows.push(locale === 'zh' ? '轨迹 GPX 已暂存' : 'Staged GPX replacement')
  }
  const mediaAdditions = proposalMediaAdditions(changes)
  if (mediaAdditions.length > 0) {
    rows.push(
      locale === 'zh'
        ? `新增媒体：${mediaAdditions.length} 个`
        : `Add media: ${mediaAdditions.length}`,
    )
  }
  const known = new Set([
    'metadata_patch',
    'waypoints',
    'geometry_staged_path',
    'media_additions',
    ...PROPOSAL_METADATA_FIELDS,
  ])
  for (const key of Object.keys(changes)) {
    if (!known.has(key)) rows.push(locale === 'zh' ? `其他改动：${key}` : `Other change: ${key}`)
  }

  if (rows.length === 0) {
    return (
      <p className="text-xs text-[var(--xr-text-muted)]">
        {locale === 'zh' ? '无可显示的改动详情' : 'No displayable change details'}
      </p>
    )
  }

  return (
    <ul className="flex flex-col gap-1 text-xs text-[var(--xr-text-secondary)]">
      {rows.map((row) => (
        <li key={row}>{row}</li>
      ))}
    </ul>
  )
}

function FieldDiffEntry({ entry }: { entry: Extract<FieldDiff, { kind: 'field' }> }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-[10px] font-medium uppercase tracking-wide text-[var(--xr-text-muted)]">
        {entry.field}
      </dt>
      <dd className="flex flex-col gap-0.5">
        {entry.before && (
          <span className="rounded-[var(--xr-radius-sm)] bg-[var(--xr-danger-soft)] px-1.5 py-0.5 text-[var(--xr-danger)] line-through">
            {truncate(entry.before)}
          </span>
        )}
        {entry.after && (
          <span className="rounded-[var(--xr-radius-sm)] bg-[var(--xr-success-soft)] px-1.5 py-0.5 text-[var(--xr-success)]">
            {truncate(entry.after)}
          </span>
        )}
        {!entry.before && !entry.after && <span className="text-[var(--xr-text-muted)]">—</span>}
      </dd>
    </div>
  )
}

function GeometryDiffEntry({
  entry,
  locale,
  t,
}: {
  entry: Extract<FieldDiff, { kind: 'geometry' }>
  locale: 'zh' | 'en'
  t: ReturnType<typeof useLocale>['t']
}) {
  return (
    <div className="flex flex-col gap-0.5" data-testid="revision-diff-geometry">
      <dt className="text-[10px] font-medium uppercase tracking-wide text-[var(--xr-text-muted)]">
        {locale === 'zh' ? '路点与轨迹' : 'Waypoints & track'}
      </dt>
      <dd className="flex flex-col gap-0.5">
        {entry.waypoints.map((change) => (
          <WaypointChangeRow
            key={`${change.type}-${change.id}`}
            change={change}
            locale={locale}
            t={t}
          />
        ))}
        {entry.trackChanged && (
          <span className="text-[var(--xr-text-secondary)]">
            {locale === 'zh' ? '轨迹已更新' : 'Track updated'}
          </span>
        )}
      </dd>
    </div>
  )
}

function WaypointChangeRow({
  change,
  locale,
  t,
}: {
  change: WaypointChange
  locale: 'zh' | 'en'
  t: ReturnType<typeof useLocale>['t']
}) {
  const kindLabel = change.kindKey ? t(`waypointKind.${change.kindKey}`) : t('waypointKind.generic')
  if (change.type === 'added') {
    return (
      <span className="text-[var(--xr-success)]">
        + {kindLabel}: {change.after}
      </span>
    )
  }
  if (change.type === 'removed') {
    return (
      <span className="text-[var(--xr-danger)]">
        - {kindLabel}: {change.before}
      </span>
    )
  }
  const sameLabel = change.before === change.after
  return (
    <span className="text-[var(--xr-text-secondary)]">
      ~ {kindLabel}:{' '}
      {sameLabel
        ? locale === 'zh'
          ? '位置已调整'
          : 'position adjusted'
        : `${change.before} → ${change.after}`}
    </span>
  )
}

function MediaDiffEntry({
  entry,
  locale,
}: {
  entry: Extract<FieldDiff, { kind: 'media' }>
  locale: 'zh' | 'en'
}) {
  return (
    <div className="flex flex-col gap-0.5" data-testid="revision-diff-media">
      <dt className="text-[10px] font-medium uppercase tracking-wide text-[var(--xr-text-muted)]">
        {locale === 'zh' ? '图片与视频' : 'Media'}
      </dt>
      <dd className="flex flex-col gap-0.5">
        {entry.added.map((m) => (
          <span key={`add-${m.id}`} className="text-[var(--xr-success)]">
            + {mediaKindLabel(m.kindKey, locale)}
          </span>
        ))}
        {entry.removed.map((m) => (
          <span key={`del-${m.id}`} className="text-[var(--xr-danger)]">
            - {mediaKindLabel(m.kindKey, locale)}
          </span>
        ))}
      </dd>
    </div>
  )
}

function mediaKindLabel(kind: string | undefined, locale: 'zh' | 'en'): string {
  if (kind === 'video') return locale === 'zh' ? '视频' : 'Video'
  return locale === 'zh' ? '图片' : 'Image'
}

function truncate(s: string, max = 240): string {
  return s.length > max ? s.slice(0, max - 1) + '…' : s
}

function proposalStatusLabel(status: ProposalPublic['status'], locale: 'zh' | 'en'): string {
  if (locale === 'zh') {
    if (status === 'open') return '待处理'
    if (status === 'approved') return '已批准'
    if (status === 'rejected') return '已拒绝'
    if (status === 'stale') return '已过时'
    if (status === 'expired') return '已过期'
    if (status === 'withdrawn') return '已撤回'
    return status
  }
  return status
}

function formatRegion(region: RoutePublic['region']): string {
  return [region?.province, region?.city, region?.district]
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter(Boolean)
    .join(' · ')
}

async function loadPanelData(
  routeId: string,
  token?: string | null,
): Promise<{
  items: EnrichedRevision[]
  media: EnrichedMedia[]
  routeProposals: EnrichedProposal[]
}> {
  const [items, mediaResult, groupedProposalResults] = await Promise.all([
    api.listRevisions(routeId, token) as Promise<EnrichedRevision[]>,
    (api.listMedia(routeId, undefined, token) as Promise<EnrichedMedia[]>).then(
      (value) => ({ status: 'fulfilled' as const, value }),
      () => ({ status: 'rejected' as const, value: [] as EnrichedMedia[] }),
    ),
    Promise.allSettled(
      PROPOSAL_STATUSES.map(
        (status) => api.listRouteProposals(routeId, status, token) as Promise<EnrichedProposal[]>,
      ),
    ),
  ])
  const media = mediaResult.status === 'fulfilled' ? mediaResult.value : []
  const dedup = new Map<string, EnrichedProposal>()
  for (const result of groupedProposalResults) {
    if (result.status !== 'fulfilled') continue
    const batch = result.value
    for (const item of batch) {
      dedup.set(item.id, item)
    }
  }
  const routeProposals = [...dedup.values()].sort(
    (a, b) => toMillis(b.resolved_at ?? b.created_at) - toMillis(a.resolved_at ?? a.created_at),
  )
  return { items, media, routeProposals }
}

function toMillis(iso: string): number {
  const ms = Date.parse(iso)
  return Number.isFinite(ms) ? ms : 0
}
